import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Cpu } from 'lucide-react';
import { Button } from '@/components/ui/button';

const SHOPIFY_INSTALL_URL = "https://apps.shopify.com/chatbot-by-emlylabs?search_id=revenue-ai-chat&surface_detail=arismate&surface_inter_position=1&surface_intra_position=2&surface_type=search";

// Official Arismate Brand Logo
const ARISMATE_LOGO = "/assets/logo.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Features', href: '/features' },
    { name: 'How It Works', href: '/how-it-works' },
    { name: 'Integrations', href: '/integrations' },
    { name: 'Contact Us', href: '/contact' },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-lg'
          : 'bg-white/90 backdrop-blur-sm'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 sm:gap-3 flex-shrink-0" 
            data-testid="navbar-logo"
          >
            <div className="flex flex-col items-center">
              <img
                src={ARISMATE_LOGO}
                alt="Arismate"
                className="px-2 py-1"
              />
              <span className="text-[10px] font-medium text-slate-500 tracking-widest -mt-1">by arismeta</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-semibold transition-all duration-300 ${
                  isActive(link.href) 
                    ? 'text-teal-600' 
                    : 'text-slate-700 hover:text-teal-600'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-3">
            {/* <Link to="/contact">
              <Button 
                variant="ghost" 
                className="text-slate-700 hover:text-teal-600 hover:bg-teal-50 font-semibold"
                data-testid="nav-contact-btn"
              >
                Contact Us
              </Button>
            </Link> */}
            <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer">
              <Button 
                className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 shadow-lg shadow-teal-600/20"
                data-testid="nav-install-btn"
              >
                <Cpu className="w-4 h-4 mr-2" />
                Install for Shopify
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-700 hover:text-teal-600 transition-colors rounded-lg hover:bg-slate-100"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="lg:hidden bg-white border-t border-slate-200 shadow-xl"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-2">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={link.href}
                    className={`block text-lg font-semibold py-3 px-4 rounded-xl transition-all ${
                      isActive(link.href)
                        ? 'text-teal-600 bg-teal-50'
                        : 'text-slate-700 hover:text-teal-600 hover:bg-slate-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-4 space-y-3 border-t border-slate-200 mt-4">
                <Link to="/contact" className="block">
                  <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:text-teal-600 hover:border-teal-500 py-6 font-semibold">
                    Contact Us
                  </Button>
                </Link>
                <a href={SHOPIFY_INSTALL_URL} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white py-6 font-semibold shadow-lg shadow-teal-600/20">
                    <Cpu className="w-5 h-5 mr-2" />
                    Install for Shopify
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
export { SHOPIFY_INSTALL_URL, ARISMATE_LOGO };
